/**
 * Cost of Living Index 2026 — household-basket multipliers across 30
 * cities. NYC = 100.0 baseline. Used by `colMultiplier()` to compute
 * "if we moved from city A to city B, our annual spend changes by Y×".
 *
 * **Source**: NUMBEO Cost of Living Plus Rent Index 2026
 * (https://www.numbeo.com/cost-of-living/rankings_current.jsp).
 * Retrieved 2026-05-08. Values are approximate snapshots and rounded to
 * one decimal. **Spot-check the destination city before relying on this
 * helper for actual relocation decisions** — NUMBEO updates indices
 * roughly quarterly + crowd-sources entries, so individual cities may
 * have drifted since retrieval.
 *
 * **What this captures**: groceries / transit / restaurants / utilities /
 * apartment rent (Plus Rent variant). Excludes housing-purchase prices,
 * income tax, healthcare insurance — those are big movers in real
 * relocation decisions but each is its own modeling problem (Phase 7
 * adds tax-aware analysis; Phase 11 polish may add a rent-vs-buy split).
 *
 * **NYC = 100 baseline rationale**: matches NUMBEO's historical
 * reference convention — when NUMBEO shifted to "use NYC as reference"
 * in 2017, indices were rescaled so NYC = 100. (Some NUMBEO pages now
 * show different bases; we lock NYC = 100 here for stability.)
 *
 * **Not in v1**: live API integration (rate limits + key management),
 * city-aggregation across metros (each NUMBEO entry is a single city),
 * regional/cost-of-housing-only splits.
 *
 * Phase 11 polish: if relocation analysis becomes a recurring decision
 * (vs the FIRE-content one-off), upgrade to the live NUMBEO API +
 * monthly snapshot job + per-city cache.
 */

export type CityCode =
  // United States
  | "NYC" // New York
  | "SFO" // San Francisco
  | "BOS" // Boston
  | "SEA" // Seattle
  | "DCA" // Washington DC
  | "SAN" // San Diego
  | "MIA" // Miami
  | "DEN" // Denver
  | "PDX" // Portland (OR)
  | "BOU" // Boulder
  | "CHI" // Chicago
  | "AVL" // Asheville
  | "AUS" // Austin
  | "MSP" // Minneapolis
  | "CLT" // Charlotte
  | "PHX" // Phoenix
  | "BNA" // Nashville
  | "ATL" // Atlanta
  | "RDU" // Raleigh-Durham
  | "PIT" // Pittsburgh
  // International
  | "LON" // London
  | "SIN" // Singapore
  | "AMS" // Amsterdam
  | "TYO" // Tokyo
  | "BER" // Berlin
  | "MAD" // Madrid
  | "LIS" // Lisbon
  | "MEX" // Mexico City
  | "BKK" // Bangkok
  | "DPS"; // Denpasar (Bali)

export interface CityCol {
  readonly city: string;
  readonly country: string;
  /** Cost of Living Plus Rent Index, NYC = 100. */
  readonly col_index: number;
}

export const COL_INDEX_2026: Readonly<Record<CityCode, CityCol>> = {
  // United States — alphabetical by code within region
  NYC: { city: "New York", country: "USA", col_index: 100.0 },
  SFO: { city: "San Francisco", country: "USA", col_index: 92.5 },
  BOS: { city: "Boston", country: "USA", col_index: 78.4 },
  SEA: { city: "Seattle", country: "USA", col_index: 75.2 },
  DCA: { city: "Washington DC", country: "USA", col_index: 73.8 },
  SAN: { city: "San Diego", country: "USA", col_index: 70.5 },
  MIA: { city: "Miami", country: "USA", col_index: 68.9 },
  DEN: { city: "Denver", country: "USA", col_index: 67.2 },
  PDX: { city: "Portland", country: "USA", col_index: 65.7 },
  BOU: { city: "Boulder", country: "USA", col_index: 64.8 },
  CHI: { city: "Chicago", country: "USA", col_index: 60.4 },
  AVL: { city: "Asheville", country: "USA", col_index: 59.7 },
  AUS: { city: "Austin", country: "USA", col_index: 58.8 },
  MSP: { city: "Minneapolis", country: "USA", col_index: 57.3 },
  CLT: { city: "Charlotte", country: "USA", col_index: 56.4 },
  PHX: { city: "Phoenix", country: "USA", col_index: 56.1 },
  BNA: { city: "Nashville", country: "USA", col_index: 55.9 },
  ATL: { city: "Atlanta", country: "USA", col_index: 55.4 },
  RDU: { city: "Raleigh-Durham", country: "USA", col_index: 53.8 },
  PIT: { city: "Pittsburgh", country: "USA", col_index: 49.2 },
  // International — alphabetical by city name
  AMS: { city: "Amsterdam", country: "Netherlands", col_index: 71.4 },
  BKK: { city: "Bangkok", country: "Thailand", col_index: 39.1 },
  BER: { city: "Berlin", country: "Germany", col_index: 64.7 },
  DPS: { city: "Denpasar (Bali)", country: "Indonesia", col_index: 31.8 },
  LIS: { city: "Lisbon", country: "Portugal", col_index: 49.4 },
  LON: { city: "London", country: "UK", col_index: 78.6 },
  MAD: { city: "Madrid", country: "Spain", col_index: 53.2 },
  MEX: { city: "Mexico City", country: "Mexico", col_index: 41.5 },
  SIN: { city: "Singapore", country: "Singapore", col_index: 79.1 },
  TYO: { city: "Tokyo", country: "Japan", col_index: 67.8 },
};

/**
 * Multiplier converting cost-of-living from city A to city B.
 *
 * Example: `colMultiplier("NYC", "PIT")` returns ~0.49 — moving to
 * Pittsburgh from NYC, an $80k/yr basket becomes ~$39k. Symmetric:
 * `colMultiplier(a, b) × colMultiplier(b, a) === 1` (within IEEE-754
 * rounding).
 *
 * Throws if either code is unknown — callers should validate input
 * before invoking. (In practice the SPA city pickers populate from
 * `Object.keys(COL_INDEX_2026)`, so unknown-code paths shouldn't
 * surface in production.)
 */
export function colMultiplier(from: CityCode, to: CityCode): number {
  const fromEntry = COL_INDEX_2026[from];
  const toEntry = COL_INDEX_2026[to];
  if (!fromEntry) {
    throw new Error(`colMultiplier: unknown 'from' city code: ${from}`);
  }
  if (!toEntry) {
    throw new Error(`colMultiplier: unknown 'to' city code: ${to}`);
  }
  return toEntry.col_index / fromEntry.col_index;
}

/** All known city codes, sorted alphabetically for SPA picker rendering. */
export const ALL_CITY_CODES: readonly CityCode[] = (
  Object.keys(COL_INDEX_2026) as CityCode[]
).sort((a, b) =>
  COL_INDEX_2026[a].city.localeCompare(COL_INDEX_2026[b].city),
);
